import { Vector3, Matrix4, Quaternion } from 'three';
import { getTransformedPosition, getTransformedDirection } from "../../Scene/Manager/TransformUtils";
import materialPool from '../../Scene/Provider/MaterialPool';
import { createCollider } from '../../Scene/Builder/PhysicsWorldBuilder';
import { threeEngine } from 'Domain/Scene/Engine/ThreeEngine';

export default class Tooth {
    static _sharedBoxGeometry = null;
    static _sharedSphereGeometry = null;

    refObj = null;
    pivot = null;
    pointSpheres = {};
    vectors = {};
    jawSide = 0;
    matrixCalculatorObj = null;
    axesCalculatorObj = null;
    lastWorldPosition = new Vector3();
    baryCenter = null;
    centralPit = null;
    rotationRoot = null;
    vecRoot = null;
    vecVestibularLingual = null;
    vecRidge = null;
    vecArchTemplateNormal = null;
    vecArchTemplateTangent = null;
    vecTangentToFacial = null;
    mouvements = [];
    movementActual = 0;
    isStepInit = true;
    pivotAlreadyPlaced = false;
    originalRefObjParent = null;
    originalRefObjPosition = null;
    originalRefObjQuaternion = null;
    originalRefObjScale = null;
    collider = null;
    body = null;
    _materialType = null;

    constructor(mesh, jawSide) {
        this.refObj = mesh;
        this.jawSide = jawSide;
    }

    async initCollider() {
        const result = await createCollider(this);
        this.collider = result.collider;
        this.body = result.body;
    }

    saveOriginalState() {
        if (this.originalRefObjParent !== null) return;
        this.originalRefObjParent = this.refObj.parent;
        this.originalRefObjPosition = this.refObj.position.clone();
        this.originalRefObjQuaternion = this.refObj.quaternion.clone();
        this.originalRefObjScale = this.refObj.scale.clone();
    }

    restoreToOriginalState() {
        if (this.originalRefObjParent === null) return;

        const mandiGroupGlobal = threeEngine.getMandiGroup();
        const targetParent = (this.jawSide === 1 && mandiGroupGlobal)
            ? mandiGroupGlobal
            : this.originalRefObjParent;

        if (this.refObj.parent !== targetParent) {
            if (this.refObj.parent) {
                this.refObj.parent.remove(this.refObj);
            }
            targetParent.add(this.refObj);
        }

        this.refObj.position.copy(this.originalRefObjPosition);
        this.refObj.quaternion.copy(this.originalRefObjQuaternion);
        this.refObj.scale.copy(this.originalRefObjScale);
        this.refObj.updateMatrix();

        this.pivot.position.set(0, 0, 0);
        this.pivot.quaternion.identity();
        this.pivot.scale.set(1, 1, 1);
        this.pivot.updateMatrix();

        this.mouvements = [];
        this.movementActual = 0;
        this.isStepInit = true;
        this.pivotAlreadyPlaced = false;
    }

    updateWorldPosition() {
        this.refObj.getWorldPosition(this.lastWorldPosition);
    }

    offsetRefObj() {
        this.refObj.parent.updateMatrixWorld(true);
        this.refObj.position.copy(this.lastWorldPosition);
        this.refObj.parent.worldToLocal(this.refObj.position);
    }

    placePivot() {
        const worldPositionBaryCenter = new Vector3();
        this.baryCenter.getWorldPosition(worldPositionBaryCenter);

        const mandiGroupGlobal = threeEngine.getMandiGroup();
        if (this.jawSide === 1 && mandiGroupGlobal) {
            mandiGroupGlobal.worldToLocal(worldPositionBaryCenter);
        }

        this.pivot.position.copy(worldPositionBaryCenter);
        this.refObj.position.sub(worldPositionBaryCenter);

        const worldQuaternion = new Quaternion();
        this.axesCalculatorObj.getWorldQuaternion(worldQuaternion);
        this.pivot.quaternion.copy(worldQuaternion);

        const inverseRotationMatrix = new Matrix4().makeRotationFromQuaternion(worldQuaternion.clone().invert());
        this.refObj.applyMatrix4(inverseRotationMatrix);
    }

    hideAllPoints() {
        Object.values(this.pointSpheres).forEach(sphere => {
            sphere.visible = false;
        });
    }

    chooseAMaterial(forceWhite = false) {
        if (!forceWhite && this.hasMovedRelativeToMaloc()) {
            this._materialType = 'beigeTooth';
            this.refObj.material = materialPool.acquire('beigeTooth');
        } else {
            this._materialType = 'tooth';
            this.refObj.material = materialPool.acquire('tooth');
        }
        this.refObj.renderOrder = -1;
    }

    ensureMaterialIsCloned() {
        const currentMaterial = this.refObj.material;
        if (currentMaterial && currentMaterial.isFromPool) {
            const clonedMaterial = currentMaterial.clone();
            clonedMaterial.isFromPool = false;
            this.refObj.material = clonedMaterial;
            this._materialType = null;
            return clonedMaterial;
        }
        return currentMaterial;
    }

    setMaterialColor(color) {
        const material = this.ensureMaterialIsCloned();
        if (material) {
            material.color.setHex(color);
        }
    }

    hasMovedRelativeToMaloc() {
        const roundToFour = (num) => Math.round(num * 10000) / 10000;
        const vectorsAreDifferent = (vecA, vecB) => (
            roundToFour(vecA[0]) !== roundToFour(vecB[0]) ||
            roundToFour(vecA[1]) !== roundToFour(vecB[1]) ||
            roundToFour(vecA[2]) !== roundToFour(vecB[2])
        );

        const baryCenterWorld = this.baryCenter ? getTransformedPosition(this.baryCenter) : null;
        const baryCenterLocal = this.baryCenter ? [this.baryCenter.position.x, this.baryCenter.position.y, this.baryCenter.position.z] : null;
        const vecRidgeWorld = this.vectors.vecRidge ? getTransformedDirection(this.vectors.vecRidge) : null;
        const vecRidgeLocal = this.vecRidge ? [this.vecRidge.x, this.vecRidge.y, this.vecRidge.z] : null;
        const vecRootWorld = this.vectors.vecRoot ? getTransformedDirection(this.vectors.vecRoot) : null;
        const vecRootLocal = this.vecRoot ? [this.vecRoot.x, this.vecRoot.y, this.vecRoot.z] : null;

        if (!baryCenterWorld || !vecRidgeWorld || !vecRootWorld) return false;

        return (
            vectorsAreDifferent(baryCenterWorld, baryCenterLocal) ||
            vectorsAreDifferent(vecRidgeWorld, vecRidgeLocal) ||
            vectorsAreDifferent(vecRootWorld, vecRootLocal)
        );
    }

    dispose() {
        if (this.refObj && this.refObj.material) {
            const material = this.refObj.material;
            if (material.isFromPool) {
                materialPool.release(this._materialType || 'tooth', material);
            } else {
                material.dispose();
            }
            this.refObj.material = null;
        }

        [this.matrixCalculatorObj, this.axesCalculatorObj, this.baryCenter].forEach(obj => {
            if (obj) {
                if (obj.material) obj.material.dispose();
                this.refObj.remove(obj);
            }
        });

        Object.values(this.pointSpheres).forEach(sphere => {
            if (sphere.material) sphere.material.dispose();
            if (sphere.geometry && !sphere.geometry.isShared) sphere.geometry.dispose();
            this.refObj.remove(sphere);
        });
        this.pointSpheres = {};

        Object.values(this.vectors).forEach(vector => {
            if (vector.line) {
                if (vector.line.geometry) vector.line.geometry.dispose();
                if (vector.line.material) vector.line.material.dispose();
            }
            if (vector.cone) {
                if (vector.cone.geometry) vector.cone.geometry.dispose();
                if (vector.cone.material) vector.cone.material.dispose();
            }
            this.refObj.remove(vector);
        });
        this.vectors = {};

        if (this.pivot) {
            this.pivot.parent?.remove(this.pivot);
            this.pivot = null;
        }
    }
}
